import {isAfter, isValid, parseISO, startOfDay} from 'date-fns';
import type {AssemblyTravelerStatusKind, TravelerVisualState} from '../data/interfaces';
import {calculateAdjustedDeadline} from './adjustedDeadline';

const ASSEMBLY_STATUS_KIND_BY_RAW_STATUS: Record<string, AssemblyTravelerStatusKind> = {
  'post-paint assembly': 'ready',
  'to paint': 'coatings',
  'to shop': 'production',
  'in production': 'production',
};

function normalizeTravelerStatus(status?: string | null): string {
  return status?.trim().toLowerCase() ?? '';
}

function getAssemblyTravelerStatusKindFromNormalizedStatus(
  normalizedStatus: string
): AssemblyTravelerStatusKind | null {
  return ASSEMBLY_STATUS_KIND_BY_RAW_STATUS[normalizedStatus] ?? null;
}

function parseComparisonDate(date?: string | Date | null): Date | null {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : parseISO(date);
  return isValid(parsedDate) ? startOfDay(parsedDate) : null;
}

function isAssemblyTravelerOverdue(
  readyToShipDeadline?: string | null,
  comparisonDate?: string | Date | null
): boolean {
  const adjustedDeadline = calculateAdjustedDeadline(readyToShipDeadline);
  const parsedComparisonDate = parseComparisonDate(comparisonDate);

  return adjustedDeadline !== null
    && parsedComparisonDate !== null
    && isAfter(parsedComparisonDate, startOfDay(adjustedDeadline));
}

export function getAssemblyTravelerStatusKind(
  status?: string | null
): AssemblyTravelerStatusKind | null {
  return getAssemblyTravelerStatusKindFromNormalizedStatus(normalizeTravelerStatus(status));
}

export function getAssemblyTravelerVisualState(
  status?: string | null,
  readyToShipDeadline?: string | null,
  comparisonDate?: string | Date | null
): TravelerVisualState | null {
  const normalizedStatus = normalizeTravelerStatus(status);
  const statusKind = getAssemblyTravelerStatusKindFromNormalizedStatus(normalizedStatus);

  if (!statusKind) {
    return null;
  }

  if (isAssemblyTravelerOverdue(readyToShipDeadline, comparisonDate)) {
    return 'overdue';
  }

  return statusKind;
}
